import { useQuery } from '@tanstack/react-query'
import { Countdown } from '@/features/onboarding/Countdown'
import type { Faculty } from '@/lib/api/auth'
import * as progression from '@/lib/api/progression'

interface Props {
  dailyGoalXp: number
  faculties: Faculty[]
  onNavigate: (id: string) => void
}

/**
 * Colonne latérale droite — 06 §5, disposition Twitter.
 *
 * Apparaît à 1005 px, largeur 350 px. Elle porte ce que Twitter y met :
 * une recherche, puis des encarts. Ici, ce sont l'objectif du jour et le
 * classement — pas des tendances : le produit n'a rien de social (D-16).
 *
 * Tout ce qui s'y trouve est ACCESSOIRE : le fil doit rester complet sans
 * cette colonne, puisqu'elle n'existe pas sur mobile.
 */
export function RightRail({ dailyGoalXp, faculties, onNavigate }: Props) {
  return (
    <aside
      aria-label="Informations complémentaires"
      // **Elle défile** — [DECISIONS §173], RG-D9ter. Haute d'un écran
      // exactement, elle coupait son dernier encart sans barre de défilement ni
      // aucun autre indice. Tout ce qu'elle porte est accessoire (06 §5) : rien
      // n'y mérite d'être ancré, la colonne entière défile donc.
      className="sticky top-0 hidden h-dvh w-[350px] shrink-0 flex-col gap-4 overflow-y-auto px-8 py-2 [scrollbar-width:thin] min-[1005px]:flex"
    >
      <label className="sr-only" htmlFor="recherche">
        Rechercher un thème
      </label>
      <input
        id="recherche"
        type="search"
        placeholder="Rechercher un thème"
        onFocus={() => { onNavigate('themes'); }}
        className="h-11 rounded-full border px-4 text-[15px] outline-none focus:border-[var(--accent)]"
        style={{
          background: 'var(--surface-2)',
          borderColor: 'transparent',
          color: 'var(--texte-1)',
        }}
      />

      {/* RG-1.11quater — le compte à rebours passe AVANT l'objectif du jour :
          c'est lui qui donne son sens au reste. */}
      <Countdown faculties={faculties} />

      <ObjectifDuRail dailyGoalXp={dailyGoalXp} onNavigate={onNavigate} />

      <section
        className="flex flex-col gap-2 rounded-2xl p-4"
        style={{ background: 'var(--surface-2)' }}
      >
        <h2 className="text-[20px] font-extrabold">Classement</h2>
        {/* RG-5.17 — l'effort, pas l'exactitude : on compte les réponses,
            les badges et les séries. Classer sur le taux de bonnes réponses
            découragerait exactement ceux qui doivent réviser. */}
        <p className="text-[15px]" style={{ color: 'var(--texte-2)' }}>
          On y compte les réponses données, les badges et les séries — jamais
          le taux de bonnes réponses.
        </p>
        <button
          type="button"
          onClick={() => { onNavigate('leaderboard'); }}
          className="self-start text-[15px] font-bold"
          style={{ color: 'var(--accent)' }}
        >
          Voir le classement
        </button>
      </section>
    </aside>
  )
}

/**
 * L'objectif du jour, dans le rail — RG-2.41, [DECISIONS §102].
 *
 * **Il annonçait la cible sans jamais dire où l'on en est.** « 100 points » ne
 * bouge pas de la journée : le seul endroit qui portait l'avancement était la
 * pastille flottante, en bas à droite, où le visage de la mascotte la recouvre
 * sur les écrans larges. Le candidat lisait donc son objectif partout et son
 * avancement nulle part.
 *
 * **La barre plutôt qu'un anneau.** Le rail est une colonne : une barre
 * horizontale y occupe la largeur disponible et se lit de loin, là où un
 * anneau de trente pixels demanderait de s'approcher.
 */
function ObjectifDuRail({
  dailyGoalXp,
  onNavigate,
}: {
  dailyGoalXp: number
  onNavigate: (id: string) => void
}) {
  const { data } = useQuery({
    queryKey: ['progression'],
    queryFn: progression.progression,
    // Le Fil invalide cette clé après chaque salve : inutile de sonder.
    staleTime: 60_000,
  })

  const fait = data?.data.objectif.xp_du_jour ?? 0
  const cible = data?.data.objectif.cible_xp ?? dailyGoalXp
  const serie = data?.data.effort.serie ?? 0
  const atteint = cible > 0 && fait >= cible

  return (
    <section
      className="flex flex-col gap-2 rounded-2xl p-4"
      style={{ background: 'var(--surface-2)' }}
    >
      <h2 className="text-[20px] font-extrabold">Ton objectif du jour</h2>

      <p className="text-[20px] font-extrabold tabular-nums">
        {fait} <span className="text-[15px] font-normal" style={{ color: 'var(--texte-2)' }}>
          {/* **Des bonnes réponses, pas des points** — §150. Le compteur
              compare `correct_count` à l'objectif ; « points » était le mot du
              temps où il comparait des XP, et il rendait le chiffre
              indéchiffrable : personne ne sait ce que vaut un point. */}
          / {cible} bonnes réponses
        </span>
      </p>

      <div
        className="h-2 w-full overflow-hidden rounded-full"
        style={{ background: 'var(--bordure)' }}
        role="progressbar"
        aria-valuenow={fait}
        aria-valuemin={0}
        aria-valuemax={cible}
      >
        <div
          className="h-full rounded-full"
          style={{
            width: `${String(cible <= 0 ? 0 : Math.min(100, Math.round((fait / cible) * 100)))}%`,
            background: 'var(--accent-fort)',
            transition: 'width 400ms ease-out',
          }}
        />
      </div>

      <p className="text-[13px]" style={{ color: 'var(--texte-2)' }}>
        {atteint && serie > 0
          ? `Objectif atteint — série de ${String(serie)} gardée.`
          : atteint
            ? 'Objectif atteint.'
            : 'Seules les bonnes réponses du Fil comptent.'}
      </p>

      <button
        type="button"
        onClick={() => { onNavigate('feed'); }}
        className="mt-1 self-start rounded-full px-4 py-1.5 text-[15px] font-bold"
        style={{ background: 'var(--accent-fort)', color: 'var(--sur-accent)' }}
      >
        Continuer
      </button>
    </section>
  )
}
